/** XeAgents.ai**/
/**
 * The country list, extracted from signup.
 *
 * These ten were written out by hand as inline <SelectItem>s in AuthDialog's
 * register form, and again as ONBOARDING_COUNTRIES in the admin owner
 * onboarding. The pilot form is the third surface that needs them, so the list
 * moved here rather than being copied a third time. Shared (not client-only)
 * because the server validates the submitted code against it.
 */
export interface Country {
  value: string;
  label: string;
}

export const COUNTRIES: Country[] = [
  { value: "US", label: "🇺🇸 United States" },
  { value: "IN", label: "🇮🇳 India" },
  { value: "GB", label: "🇬🇧 United Kingdom" },
  { value: "CA", label: "🇨🇦 Canada" },
  { value: "AU", label: "🇦🇺 Australia" },
  { value: "DE", label: "🇩🇪 Germany" },
  { value: "FR", label: "🇫🇷 France" },
  { value: "SG", label: "🇸🇬 Singapore" },
  { value: "AE", label: "🇦🇪 United Arab Emirates" },
  { value: "NL", label: "🇳🇱 Netherlands" },
];

const CODES = new Set(COUNTRIES.map((c) => c.value));

/**
 * Normalise a submitted country code, or null when it isn't one we offer.
 *
 * Returns null rather than falling back to a default: storing "US" for someone
 * who never said "US" invents an answer, and every downstream plan/currency
 * decision then reads it as though they had.
 */
export function normalizeCountry(code: string | null | undefined): string | null {
  if (!code) return null;
  const upper = code.trim().toUpperCase();
  return CODES.has(upper) ? upper : null;
}

/**
 * Best-effort country for a browser timezone, used only as a fallback when the
 * server-side lookup gives nothing. Covers the offered countries; anything else
 * returns null so the field stays empty.
 */
const TIMEZONE_COUNTRY: Record<string, string> = {
  "Asia/Kolkata": "IN",
  "Asia/Calcutta": "IN",
  "Europe/London": "GB",
  "Europe/Berlin": "DE",
  "Europe/Paris": "FR",
  "Europe/Amsterdam": "NL",
  "Asia/Singapore": "SG",
  "Asia/Dubai": "AE",
  "Australia/Sydney": "AU",
  "Australia/Melbourne": "AU",
  "Australia/Brisbane": "AU",
  "Australia/Perth": "AU",
  "America/Toronto": "CA",
  "America/Vancouver": "CA",
  "America/Edmonton": "CA",
  "America/Winnipeg": "CA",
  "America/New_York": "US",
  "America/Chicago": "US",
  "America/Denver": "US",
  "America/Los_Angeles": "US",
  "America/Phoenix": "US",
  "America/Anchorage": "US",
  "Pacific/Honolulu": "US",
};

export function countryFromTimezone(timeZone: string | null | undefined): string | null {
  if (!timeZone) return null;
  return TIMEZONE_COUNTRY[timeZone] ?? null;
}
